import { useEffect, useState, useCallback, useRef } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getPosts, createPost, updatePost, deletePost,
  uploadPostImage, uploadPostAttachment, deletePostAttachment,
} from '../services/ApiService';
import type { Post } from '../types/models';
import Modal from '../components/Modal';
import PostComments from '../components/PostComments';
import PostAttachments from '../components/PostAttachments';
import MeetingsTab from '../components/MeetingsTab';
import {
  Plus, Pencil, Trash2, Newspaper, ImagePlus, Paperclip, X, Loader2,
  CalendarDays, Clock, MapPin,
} from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

type PageTab = 'posts' | 'meetings';

export default function FeedPage() {
  const { currentOrg, isBoardMember, updateLastSeen } = useOrganization();
  const { user } = useAuth();
  const [activePageTab, setActivePageTab] = useState<PageTab>('posts');

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Anslagstavla</h1>
          <p className="text-gray-500 text-sm mt-1">Nyheter, meddelanden och möten</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1">
        <button
          data-tour="posts-tab"
          onClick={() => setActivePageTab('posts')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activePageTab === 'posts'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Newspaper className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Inlägg
        </button>
        <button
          data-tour="meetings-tab"
          onClick={() => setActivePageTab('meetings')}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            activePageTab === 'meetings'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <CalendarDays className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
          Möten
        </button>
      </div>

      {activePageTab === 'posts' ? (
        <PostsContent
          currentOrg={currentOrg}
          user={user}
          isBoardMember={isBoardMember}
          updateLastSeen={updateLastSeen}
          onSwitchToMeetings={() => setActivePageTab('meetings')}
        />
      ) : (
        <MeetingsTab />
      )}
    </div>
  );
}

function PostsContent({
  currentOrg,
  user,
  isBoardMember,
  updateLastSeen,
  onSwitchToMeetings,
}: {
  currentOrg: { id: string; name: string } | null;
  user: { id: string } | null;
  isBoardMember: boolean;
  updateLastSeen: (section: string) => Promise<void>;
  onSwitchToMeetings: () => void;
}) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<{ id: string; fileName: string }[]>([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPosts = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const data = await getPosts(currentOrg.id);
      setPosts(data);
      setFetchError('');
    } catch {
      setPosts([]);
      setFetchError('Kunde inte ladda inlägg. Kontrollera anslutningen och försök igen.');
    } finally {
      setLoading(false);
    }
  }, [currentOrg]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);
  useEffect(() => { if (currentOrg) updateLastSeen('posts'); }, [currentOrg, updateLastSeen]);

  const resetForm = () => {
    setTitle('');
    setContent('');
    setImageFile(null);
    setImagePreview('');
    setAttachmentFiles([]);
    setExistingAttachments([]);
    setRemovedAttachmentIds([]);
  };

  const openCreate = () => {
    setEditingPost(null);
    resetForm();
    setShowModal(true);
  };

  const openEdit = (post: Post) => {
    setEditingPost(post);
    setTitle(post.title);
    setContent(post.content);
    setImageFile(null);
    setImagePreview(post.imageUrl || '');
    setAttachmentFiles([]);
    setExistingAttachments(
      (post.attachments ?? []).map(a => ({ id: a.id, fileName: a.fileName }))
    );
    setRemovedAttachmentIds([]);
    setShowModal(true);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview('');
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleAttachmentSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setAttachmentFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewAttachment = (idx: number) => {
    setAttachmentFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const removeExistingAttachment = (id: string) => {
    setExistingAttachments(prev => prev.filter(a => a.id !== id));
    setRemovedAttachmentIds(prev => [...prev, id]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    setSaving(true);
    setUploading(true);

    try {
      let postId: string;

      if (editingPost) {
        postId = editingPost.id;
        await updatePost(currentOrg.id, editingPost.id, { title, content });
        for (const attId of removedAttachmentIds) {
          await deletePostAttachment(currentOrg.id, editingPost.id, attId);
        }
      } else {
        const created = await createPost(currentOrg.id, { title, content });
        postId = created.id;
        track(AnalyticsEvents.PostCreated, {
          orgId: currentOrg.id, hasImage: !!imageFile, attachmentCount: attachmentFiles.length,
        });
      }

      if (imageFile) {
        await uploadPostImage(currentOrg.id, postId, imageFile);
      }

      for (const file of attachmentFiles) {
        await uploadPostAttachment(currentOrg.id, postId, file);
      }

      setShowModal(false);
      resetForm();
      fetchPosts();
    } catch (err) {
      console.error('Post save error:', err);
      alert(err instanceof Error ? err.message : 'Något gick fel. Försök igen.');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  const handleDelete = async (postId: string) => {
    if (!currentOrg) return;
    if (!window.confirm('Är du säker på att du vill ta bort detta inlägg?')) return;
    try {
      await deletePost(currentOrg.id, postId);
      fetchPosts();
    } catch {
      alert('Kunde inte ta bort inlägget.');
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Laddar...</div>;

  return (
    <>
      {fetchError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
          {fetchError}
        </div>
      )}
      {isBoardMember && (
        <div className="flex justify-end mb-4">
          <button data-tour="post-add" onClick={openCreate} className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium transition-colors text-sm">
            <Plus className="w-4 h-4" />
            Nytt inlägg
          </button>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="text-center py-16">
          <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Inga inlägg ännu</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => post.postType === 'meeting' && post.meeting ? (
            <MeetingPostCard
              key={post.id}
              post={post}
              formatDate={formatDate}
              isBoardMember={isBoardMember}
              onDelete={handleDelete}
              onNavigate={onSwitchToMeetings}
            />
          ) : (
            <article key={post.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
              {post.imageUrl && (
                <div className="relative w-full aspect-[2.5/1] bg-gray-100 overflow-hidden">
                  <img
                    src={post.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">{post.title}</h2>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-semibold text-teal-700">
                        {post.author.fullName?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="text-sm text-gray-500">{post.author.fullName}</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-400">{formatDate(post.createdAt)}</span>
                    </div>
                  </div>
                  {isBoardMember && (
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(post)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                        <Pencil className="w-4 h-4 text-gray-400" />
                      </button>
                      <button onClick={() => handleDelete(post.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                <PostAttachments attachments={post.attachments ?? []} />
                <PostComments
                  orgId={currentOrg?.id ?? ''}
                  postId={post.id}
                  initialComments={post.comments ?? []}
                />
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingPost ? 'Redigera inlägg' : 'Nytt inlägg'}>
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rubrik</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Innehåll</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              required
              rows={5}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bild</label>
            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border border-gray-200">
                <img src={imagePreview} alt="" className="w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute top-2 right-2 p-1 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:text-gray-600 hover:border-gray-300 transition-colors"
              >
                <ImagePlus className="w-5 h-5" />
                <span className="text-sm">Klicka för att välja en bild</span>
              </button>
            )}
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Bilagor</label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
            >
              <Paperclip className="w-4 h-4" />
              Lägg till filer
            </button>
            <input ref={fileInputRef} type="file" multiple onChange={handleAttachmentSelect} className="hidden" />

            {(existingAttachments.length > 0 || attachmentFiles.length > 0) && (
              <div className="mt-2 space-y-1.5">
                {existingAttachments.map(att => (
                  <div key={att.id} className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg text-sm">
                    <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-700 truncate flex-1">{att.fileName}</span>
                    <button type="button" onClick={() => removeExistingAttachment(att.id)} className="p-0.5 rounded hover:bg-red-50 transition-colors">
                      <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
                {attachmentFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 rounded-lg text-sm">
                    <Paperclip className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                    <span className="text-gray-700 truncate flex-1">{file.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(0)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                    </span>
                    <button type="button" onClick={() => removeNewAttachment(idx)} className="p-0.5 rounded hover:bg-red-50 transition-colors">
                      <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
              Avbryt
            </button>
            <button type="submit" disabled={saving} className="flex items-center gap-2 px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? (uploading ? 'Laddar upp...' : 'Sparar...') : editingPost ? 'Uppdatera' : 'Publicera'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}

function MeetingPostCard({ post, formatDate, isBoardMember, onDelete, onNavigate }: {
  post: Post;
  formatDate: (d: string) => string;
  isBoardMember: boolean;
  onDelete: (id: string) => void;
  onNavigate: () => void;
}) {
  const meeting = post.meeting!;
  const formatMeetingDate = (d: string) =>
    new Date(d).toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' });
  const formatTime = (d: string) =>
    new Date(d).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

  return (
    <article className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4 flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <CalendarDays className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-teal-100 text-xs font-medium uppercase tracking-wider">Mötesinbjudan</p>
          <h2 className="text-lg font-semibold text-white truncate">{meeting.title}</h2>
        </div>
        {isBoardMember && (
          <button
            onClick={() => onDelete(post.id)}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
          >
            <Trash2 className="w-4 h-4 text-white/70" />
          </button>
        )}
      </div>
      <div className="p-6">
        {meeting.description && (
          <p className="text-gray-700 mb-4 leading-relaxed">{meeting.description}</p>
        )}
        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
          <span className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-teal-600" />
            {formatMeetingDate(meeting.meetingDate)}
          </span>
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-teal-600" />
            {formatTime(meeting.meetingDate)}
          </span>
          {meeting.location && (
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-teal-600" />
              {meeting.location}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center text-xs font-semibold text-teal-700">
              {post.author.fullName?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <span className="text-sm text-gray-500">{post.author.fullName}</span>
            <span className="text-gray-300">|</span>
            <span className="text-sm text-gray-400">{formatDate(post.createdAt)}</span>
          </div>
          <button
            onClick={onNavigate}
            className="flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800 transition-colors"
          >
            Gå till mötet
            <CalendarDays className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}
